/**
 * Search-specific types for agent management.
 */

import type { AgentSourceReference } from '@lumapps/agent-hub-sources/types';
import type { AgentTool, AgentToolOrSourceItem } from '@lumapps/agent-hub-tools/types';
import type { ActionableItemResponse, MicroAppResponse } from '@lumapps/ask-ai-rules/types';
import type { AskAiSource } from '@lumapps/ask-ai-sources/types';
import type { ServerListResponse } from '@lumapps/base-api';
import type { Icon } from '@lumapps/base-api/utils/icons';
import type { GroupReference } from '@lumapps/groups/types';
import type { InputVariableDefinition } from '@lumapps/micro-apps-builder/types';
import type { TranslatableObject } from '@lumapps/translations';
import type { User, UserReference } from '@lumapps/user/types';

// ============================================================================
// Agent Core Types (moved from agent-library)
// ============================================================================

/**
 * The type of an agent.
 */
export enum AgentType {
    /** A RAG agent. */
    RAG = 'rag',
    /** A react stateless agent. */
    REACT_STATELESS = 'react_stateless',
    /** A static answer agent. */
    STATIC_ANSWER = 'static_answer',
    /** An external agent. */
    EXTERNAL = 'external',
    /** A third-party agent connected over the A2A protocol. */
    EXTERNAL_A2A = 'external_a2a',
}

/**
 * Status of the agent ("deleted" is not listed because "deleted" entities are never shown by the API).
 */
export enum AgentStatus {
    /** The agent is live. */
    LIVE = 'live',
    /** The agent is a draft. */
    DRAFT = 'draft',
}

/**
 * The base properties of an agent.
 */
export interface AgentBase {
    /** The agent identifier. */
    id: string;
    /** The agent name. */
    name: TranslatableObject;
}

/** Reference for micro app type entities */
export type AgentMicroAppReference = {
    id: string;
    name: TranslatableObject;
    description?: TranslatableObject;
    inputVariableDefinitions: InputVariableDefinition[];
};

/** References given by the agent to use in tools */
export type AgentReferencedEntities = {
    microapp?: AgentMicroAppReference[];
};

/**
 * The properties that are common to all agent types.
 */
export interface AgentCommonProperties extends AgentBase {
    /** The agent status. */
    status: AgentStatus;
    /** The agent type. */
    type: AgentType;
    /** The agent description to display in the agent library. */
    description: TranslatableObject;
    /**
     * The agent's expertise.
     * This is the field that will be used by the agent router to
     * define which agent is the most appropriate to answer the user's query
     */
    expertise: string;
    /** The agent icon name. Must be the name of a material-design icon. */
    iconName: string;
    /** The background color of the agent icon, in rgba css format. */
    iconBackgroundColor: string;
    /** The groups that can see the agent. */
    visibleByGroups: GroupReference[];
    /** Whether the current user can update the agent. */
    canUpdate: boolean;
    /** Whether the current user can delete the agent. */
    canDelete: boolean;
    /** True if the agent is built-in (native), false if it is user-defined. Read-only from API. */
    isNative: boolean;
    /** The time and date when the agent was created. It is absent for built-in agents. */
    createdAt?: string;
    /** The time and date when the agent was last updated. It is absent for built-in agents. */
    updatedAt?: string;
    /** The user who created the agent. It is absent for built-in agents. */
    createdBy?: UserReference;
    /** The user who last updated the agent. It is absent for built-in agents. */
    updatedBy?: UserReference;
    /** The tools that the agent can use. */
    tools?: AgentTool[];
    /** Up to `MAX_CONVERSATION_STARTERS` suggestions to be displayed on conversation initiation */
    conversationStarters?: TranslatableObject[];
    /** Reference to entities needed for tools to work properly */
    referencedEntities?: AgentReferencedEntities;
}

/**
 * A RAG (Retrieval Augmented Generation) agent.
 */
export interface RagAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.RAG;
    /** The agent prompt, instructing what it should do. */
    prompt: string;
    /** The sources on which the agent relies to fetch documents. */
    sources: AgentSourceReference[];
}

/**
 * Base properties for a static answer agent.
 */
type StaticAnswerAgentBase = AgentCommonProperties & {
    /** The agent type. */
    type: AgentType.STATIC_ANSWER;
    /** The answer with which the agent always replies (LocalizedString). */
    staticAnswer: TranslatableObject;
};

/**
 * A static answer agent that displays a micro app.
 */
type StaticAnswerAgentWithMicroApp = StaticAnswerAgentBase & {
    /** ID of the micro app displayed by this agent. */
    displayedMicroAppId: string;
    /** The details of the microapp displayed by the agent. */
    displayedMicroApp: MicroAppResponse;
    displayedUserId?: never;
    displayedUser?: never;
};

/**
 * A static answer agent that displays a user.
 */
type StaticAnswerAgentWithUser = StaticAnswerAgentBase & {
    /** ID of the user displayed by this agent. */
    displayedUserId: string;
    /** The details of the user displayed by the agent. */
    displayedUser: UserReference;
    displayedMicroAppId?: never;
    displayedMicroApp?: never;
};

/**
 * A static answer agent that displays with no actionable items.
 */
type StaticAnswerAgentWithoutActionableItem = StaticAnswerAgentBase & {
    displayedMicroAppId?: never;
    displayedMicroApp?: never;
    displayedUserId?: never;
    displayedUser?: never;
};

/**
 * An agent that always replies with the same static answer.
 *
 * This agent can optionally display either a micro app or a user (but not both).
 */
export type StaticAnswerAgent =
    | StaticAnswerAgentWithMicroApp
    | StaticAnswerAgentWithUser
    | StaticAnswerAgentWithoutActionableItem;

/**
 * A stateless agent that can use tools.
 */
export interface ReactStatelessAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.REACT_STATELESS;
    /** The agent prompt, instructing what the agent should do. */
    prompt: string;
    /** The sources on which the agent relies to fetch documents (knowledge). */
    sources?: AgentSourceReference[];
}

/**
 * An external agent that redirects users to an external URL.
 */
export interface ExternalAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.EXTERNAL;
    /** The external URL to redirect users to. */
    redirectUrl: string;
}

/**
 * Reference to the connector authorising calls to an A2A agent (id + display name).
 * Returned read-only by the API for display; absent from create/update requests.
 */
export interface AgentConnectorReference {
    /** The connector identifier. */
    id: string;
    /** The connector display name. */
    name: string;
}

/**
 * A third-party agent connected over the A2A protocol.
 */
export interface ExternalA2aAgent extends AgentCommonProperties {
    /** The agent type. */
    type: AgentType.EXTERNAL_A2A;
    /** URL of the agent card describing the A2A agent. */
    a2aCardUrl: string;
    /** Identifier of the connector authorising calls to the agent. */
    connectorId: string;
    /** Read-only details (id + name) of the connector, returned by the API for display. Absent in requests. */
    connectorReference?: AgentConnectorReference;
}

/**
 * An agent.
 */
export type Agent = RagAgent | StaticAnswerAgent | ReactStatelessAgent | ExternalAgent | ExternalA2aAgent;

/** Object received when listing available AI Agents: GET call to /agents */
export type AiAgentsListDTO = ServerListResponse<Agent>;

/** Agent sub type for built-in agent. Can be any type of agent (rag, static...) */
export type NativeAgent = Agent & { isNative: true };

// ============================================================================
// Agent Input Types (for Create/Update operations)
// ============================================================================

/**
 * Base input fields for creating/updating agents.
 * Derived from AgentCommonProperties by removing read-only fields.
 */
export type AgentInputBase = Pick<
    AgentCommonProperties,
    'name' | 'description' | 'expertise' | 'iconName' | 'iconBackgroundColor' | 'status' | 'conversationStarters'
> & {
    /** The groups that can see the agent (input format). */
    visibleByGroups?: { id: string }[];
};

/**
 * Input for creating/updating a RAG agent.
 */
export type RagAgentInput = AgentInputBase & Pick<RagAgent, 'type' | 'prompt' | 'sources'>;

/**
 * Input for creating/updating a static answer agent.
 */
export type StaticAnswerAgentInput = AgentInputBase &
    Pick<StaticAnswerAgentBase, 'type' | 'staticAnswer'> & {
        /** ID of the micro app displayed by this agent. Mutually exclusive with displayedUserId. */
        displayedMicroAppId?: string;
        /** ID of the user displayed by this agent. Mutually exclusive with displayedMicroAppId. */
        displayedUserId?: string;
    };

/**
 * Input for creating/updating a react stateless agent.
 */
export type ReactStatelessAgentInput = AgentInputBase &
    Pick<ReactStatelessAgent, 'type' | 'prompt' | 'sources'> & {
        /** The tools that the agent can use (input format). */
        tools: { id: string }[];
    };

/**
 * Input for creating/updating an external agent.
 */
export type ExternalAgentInput = AgentInputBase & Pick<ExternalAgent, 'type' | 'redirectUrl'>;

/**
 * Input for creating/updating an external A2A agent.
 */
export type ExternalA2aAgentInput = AgentInputBase & Pick<ExternalA2aAgent, 'type' | 'a2aCardUrl' | 'connectorId'>;

/**
 * Union type for agent input (used for both create and update operations).
 */
export type AgentInput =
    | RagAgentInput
    | StaticAnswerAgentInput
    | ReactStatelessAgentInput
    | ExternalAgentInput
    | ExternalA2aAgentInput;

/**
 * Parameters for listing agents.
 */
export interface ListAgentsParams {
    /** Filter by agent status. Can be a single status or array for multiple statuses. */
    status?: AgentStatus | AgentStatus[];
    /** Cursor for pagination. */
    cursor?: string;
    /** Maximum number of results to return. */
    maxResults?: number;
    /** Sort order (e.g., '-status' for descending status). */
    sortOrder?: string;
    /** Filter listed agents by type. Can be specified multiple times for OR filtering */
    type?: AgentType[];
    /** Search term for filtering agents. */
    search?: string;
    /** Field to search in (name, description, or name_or_description). */
    searchIn?: 'name' | 'description' | 'name_or_description';
    /** Filter by agent creators. Array of user IDs. */
    createdBy?: string[];
    /** Filter by agent sources. Array of source UUIDs. */
    sourceIds?: string[];
    /** Filter agent by tools. Array of toolIds. */
    toolTechnicalNames?: string[];
    /** Filter by native/user-created agents. When false, excludes native agents. */
    isNative?: boolean;
}

/**
 * Form input values for creating/updating an agent.
 * Uses a simplified flat structure for form handling.
 */
export interface AgentFormInput {
    /** Agent ID (for updates) */
    id?: string;
    /** Agent name */
    name: TranslatableObject;
    /** Agent icon (material design icon) */
    icon: Icon;
    /** Agent description, displayed in the agent library */
    description: TranslatableObject;
    /** Agent expertise, used by the agent router to pick the most appropriate agent */
    expertise: string;
    /** Agent status */
    status: AgentStatus;
    /** Groups that can see the agent */
    visibleByGroups?: Agent['visibleByGroups'];
    /** Agent type (required) */
    type: Agent['type'];
    /** Custom prompt for RAG and REACT_STATELESS agents */
    prompt?: string;
    /** Sources for RAG and REACT_STATELESS agents */
    sources?: { id: string; title?: string }[];
    /** Tools for REACT_STATELESS agents */
    tools?: AgentTool[];
    /** Static answer for static answer agents (LocalizedString) */
    staticAnswer?: TranslatableObject;
    /** Actionable item for static answer agents */
    actionableItem?: ActionableItemResponse;
    /** Up to `MAX_CONVERSATION_STARTERS` suggestions to be displayed on conversation initiation */
    conversationStarters?: TranslatableObject[];
    /** Redirect URL for external agents */
    redirectUrl?: string;
    /** Agent Card URL for external A2A agents */
    a2aCardUrl?: string;
    /** Selected connector (id + name) for external A2A agents, used by the connector picker. */
    connector?: AgentConnectorReference;
}

/**
 * Default values for a new agent form (type is selected by user during creation).
 */
export type AgentFormDefaultValues = Omit<AgentFormInput, 'type'>;

/**
 * List agents types
 */

/** Sort keys for agent listing */
export enum SortKeys {
    AZ_TITLE = 'title',
    ZA_TITLE = '-title',
    MOST_RECENT = 'updatedAt',
    LEAST_RECENT = '-updatedAt',
}

export enum SearchInKeys {
    NAME = 'name',
    DESCRIPTION = 'description',
    NAME_OR_DESCRIPTION = 'name_or_description',
}

/** Generic list params object */
export interface ListAgentsParamsDTO {
    /** Statuses to retrieve */
    status?: AgentStatus[];
    /** Maximum count of result per page */
    maxResults?: number;
    /** Current page cursor */
    cursor?: string;
    /** How to sort the results */
    sort?: SortKeys;
    /** Search term for filtering agents */
    search?: string;
    /** Field to search in (name, description, or name_or_description). */
    searchIn?: SearchInKeys;
    /** Filter by agent types */
    type?: AgentType[];
    /** Filter by agent creators (user IDs) */
    createdBy?: string[];
    /** Filter by agent sources (source UUIDs) */
    sourceIds?: string[];
    /** Filter by tool technical names (tool IDs) */
    toolTechnicalNames?: string[];
    /** Sort order (e.g., '-updatedAt' for descending) */
    sortOrder?: string;
    /** Filter by native/user-created agents. When false, excludes native agents. */
    isNative?: boolean;
    /** The languages in which to fetch the agents */
    acceptLanguage?: string;
}

/**
 * Option for filtering agents by type.
 * Can represent a single type or a group of types (for UX simplification).
 *
 * @example Single type
 * { id: 'rag', label: 'AI Search Assistants', types: [AgentType.RAG] }
 *
 * @example Grouped types
 * { id: 'ai-agents', label: 'AI Agents', types: [AgentType.REACT_STATELESS, AgentType.EXTERNAL], isGroup: true }
 */
export interface AgentTypeOption {
    /** Unique identifier for this option (e.g., "rag", "ai-agents") */
    id: string;
    /** Display label */
    label: string;
    /**
     * Agent types this option represents.
     * Single type: [AgentType.RAG]
     * Grouped types: [AgentType.REACT_STATELESS, AgentType.EXTERNAL]
     */
    types: AgentType[];
    /** Whether this represents multiple types (optional, for clarity) */
    isGroup?: boolean;
}

/** Option for filtering agents by status */
export interface AgentStatusOption {
    /** Unique identifier (matches AgentStatus value) */
    id: AgentStatus;
    /** Display label */
    label: string;
}

/**
 * "Created by" filter (agent origin) options.
 * There is no dedicated backend param: each option maps frontend-side onto existing
 * `isNative` / `type` params (see `useAgentFilters`):
 * - LumApps ⇒ isNative: true
 * - Workspace admins ⇒ isNative: false
 * - External system ⇒ type: [EXTERNAL, EXTERNAL_A2A]
 */
export enum AgentOrigin {
    LUMAPPS = 'lumapps',
    WORKSPACE_ADMINS = 'workspace-admins',
    EXTERNAL_SYSTEM = 'external-system',
}

/** Option for the "Created by" filter (agent origin) */
export interface AgentOriginOption {
    /** Unique identifier (matches AgentOrigin value) */
    id: AgentOrigin;
    /** Display label */
    label: string;
}

/** Agent filter values */
export interface AgentFiltersValue {
    /** Agent type IDs to filter by */
    type?: AgentType[];
    /** Agent statuses to filter by */
    status?: AgentStatusOption[];
    /** Authors (users) to filter by */
    createdBy?: User[];
    /** Origin ("Created by") to filter by — single-select (LumApps / Workspace admins / External system) */
    origin?: AgentOriginOption;
    /** Source IDs to filter by */
    sourceIds?: AskAiSource[];
    /** Tools to filter by */
    tools?: AgentTool[];
    /** Combined sources and tools to filter by */
    sourcesAndTools?: AgentToolOrSourceItem[];
}

/**
 * A conversation starter that prompts users with a suggested question or action.
 */
export interface ConversationStarter {
    /** Unique identifier for the conversation starter. */
    id: string;
    /** The agent ID this starter belongs to. */
    agentId: string;
    /** Position/order in the list. */
    position: number;
    /** The translatable text of the conversation starter. */
    text: TranslatableObject;
    /** The name of the agent icon */
    iconName: string;
}
